/**
 * EIP-712 typed-data review for dApp signature requests (ENTX-A-4).
 *
 * A vault signature over typed data is checked on-chain through ERC-1271, so
 * an EIP-2612 `Permit` or a Uniswap Permit2 message signed by the vault grants
 * a token allowance outside the proposal / policy pipeline. Instead of showing
 * a JSON blob, decode what the signature authorises (token, spender, amount,
 * deadline) and refuse typed data bound to another chain.
 *
 * What gets signed is the digest the backend computes with ethers'
 * TypedDataEncoder, which (a) derives the primary type from the `types` graph
 * and ignores the declared `primaryType`, and (b) accepts more spellings of
 * numbers and addresses than a strict regex (leading '+', '0X', zero padding,
 * an address without '0x'). The parsing here follows the same rules
 * (ethers' getBigInt / getAddress semantics), and anything that looks like a
 * token permission but cannot be fully decoded is marked `undecodable` so the
 * request cannot be approved (fail closed).
 *
 * Pure and never throws.
 */

/** Canonical Permit2 deployment (same address on every chain). */
export const PERMIT2_ADDRESS = '0x000000000022d473030f116ddee9f6b43ac78ba3';

/** Allowances at or above 2^159 are effectively unlimited (uint160 max for Permit2). */
const UNLIMITED_THRESHOLD = 1n << 159n;
const MAX_UINT256 = (1n << 256n) - 1n;
const MAX_FIELDS = 60;
const MAX_FIELD_LENGTH = 200;
/** Longer numeric strings are not parsed (fail closed); ethers has no cap, padding is allowed. */
const MAX_NUMERIC_STRING_LENGTH = 1024;

export type TypedDataApprovalKind =
  | 'permit'
  | 'dai_permit'
  | 'permit2_single'
  | 'permit2_batch'
  | 'permit2_transfer'
  | 'permit2_batch_transfer';

export interface TypedDataApproval {
  kind: TypedDataApprovalKind;
  /** Lower-cased token contract. */
  token: string;
  /** Lower-cased spender / operator that receives the allowance. */
  spender: string;
  /** Decimal base units, or null when the permit is all-or-nothing (DAI). */
  amount: string | null;
  unlimited: boolean;
  /** Unix seconds after which the permission/signature expires, if any. */
  deadline: string | null;
}

/**
 * Why a typed-data request must not be approved even though it parsed:
 * - PERMIT_NOT_DECODED: it is a token permission (Permit / Permit2 / DAI
 *   permit, by its primary type or its domain) but not every allowance in it
 *   could be decoded, so the user cannot see what they would grant;
 * - PRIMARY_TYPE_MISMATCH: the declared `primaryType` is not the root of the
 *   `types` graph, which is what is actually hashed and signed;
 * - TYPES_INVALID: `types` is missing or has no single root type, so what
 *   would be signed cannot be determined.
 */
export type TypedDataUndecodable = 'PERMIT_NOT_DECODED' | 'PRIMARY_TYPE_MISMATCH' | 'TYPES_INVALID';

export interface DecodedTypedData {
  /** The primary type that is hashed (derived from `types` when possible). */
  primaryType: string;
  domain: {
    name: string | null;
    version: string | null;
    /** null when the domain carries no chainId. */
    chainId: number | null;
    verifyingContract: string | null;
  };
  approvals: TypedDataApproval[];
  /** A marketplace order (Seaport, Blur, …) — can transfer assets on fill. */
  isOrder: boolean;
  /** Flattened message for display (capped). */
  fields: Array<{ path: string; value: string }>;
  /** Non-null: shown for review, but approval is disabled (fail closed). */
  undecodable: TypedDataUndecodable | null;
}

export type TypedDataParse =
  | { ok: true; data: DecodedTypedData }
  | { ok: false; error: 'INVALID_JSON' | 'INVALID_SHAPE' | 'INVALID_CHAIN_ID' };

type Json = null | boolean | number | string | Json[] | { [key: string]: Json };

function isRecord(value: unknown): value is Record<string, Json> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Lower-cased 0x address, accepting what ethers' getAddress accepts for a hex
 * address: with or without the '0x' prefix (not '0X'), any letter case. The
 * EIP-55 checksum is not checked here (a bad checksum makes the backend's
 * hashing fail, so nothing could be signed). ICAP addresses are not decoded:
 * they return null, which makes a permit undecodable (fail closed).
 */
function toAddress(value: unknown): string | null {
  if (typeof value !== 'string' || !/^(0x)?[0-9a-fA-F]{40}$/.test(value)) return null;
  return `0x${value.slice(value.length - 40).toLowerCase()}`;
}

/**
 * ethers' getBigInt: integers from a JSON number (safe integers only) or a
 * string that JavaScript's BigInt() accepts (decimal with an optional sign,
 * 0x/0X hex, 0o, 0b, leading zeros, surrounding whitespace); '' is refused.
 */
function toBigIntLikeEthers(value: unknown): bigint | null {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) ? BigInt(value) : null;
  }
  if (typeof value !== 'string' || value === '' || value.length > MAX_NUMERIC_STRING_LENGTH) {
    return null;
  }
  try {
    if (value.startsWith('-') && value[1] !== '-') return -BigInt(value.substring(1));
    return BigInt(value);
  } catch {
    return null;
  }
}

/** uint256 as ethers' encoder accepts it; null when not an integer in [0, 2^256). */
function toUint(value: unknown): bigint | null {
  const n = toBigIntLikeEthers(value);
  return n === null || n < 0n || n > MAX_UINT256 ? null : n;
}

/** Truthiness exactly as ethers encodes an EIP-712 `bool` (`!!value`). */
function toBoolLikeEthers(value: unknown): boolean {
  return Boolean(value);
}

const ATOMIC_TYPE_RE =
  /^(address|bool|string|bytes|bytes([1-9]|[12]\d|3[0-2])|u?int|u?int(8|16|24|32|40|48|56|64|72|80|88|96|104|112|120|128|136|144|152|160|168|176|184|192|200|208|216|224|232|240|248|256))$/;

/** The struct name a field type refers to (array suffixes stripped). */
function baseTypeName(type: string): string {
  const idx = type.indexOf('[');
  return idx === -1 ? type : type.slice(0, idx);
}

/**
 * The primary type ethers' TypedDataEncoder would hash: the single struct in
 * `types` (EIP712Domain excluded, as the backend does) that no other struct
 * refers to. null when `types` is missing, malformed, refers to an unknown
 * type, or has no single root.
 */
function derivePrimaryType(types: unknown): string | null {
  if (!isRecord(types)) return null;
  const names = Object.keys(types).filter((name) => name !== 'EIP712Domain');
  if (names.length === 0) return null;
  const referenced = new Set<string>();
  for (const name of names) {
    const fields = types[name];
    if (!Array.isArray(fields)) return null;
    for (const field of fields) {
      if (!isRecord(field) || typeof field['name'] !== 'string') return null;
      const type = field['type'];
      if (typeof type !== 'string') return null;
      const base = baseTypeName(type);
      if (ATOMIC_TYPE_RE.test(base)) continue;
      if (base === name || !names.includes(base)) return null;
      referenced.add(base);
    }
  }
  const roots = names.filter((name) => !referenced.has(name));
  return roots.length === 1 ? (roots[0] ?? null) : null;
}

function parseChainId(value: unknown): number | null | 'invalid' {
  if (value === undefined || value === null) return null;
  const id = toUint(value);
  if (id === null || id === 0n || id > BigInt(Number.MAX_SAFE_INTEGER)) return 'invalid';
  return Number(id);
}

function flatten(value: Json, path: string, out: Array<{ path: string; value: string }>): void {
  if (out.length >= MAX_FIELDS) return;
  if (Array.isArray(value)) {
    value.forEach((item, i) => {
      flatten(item, `${path}[${String(i)}]`, out);
    });
    return;
  }
  if (isRecord(value)) {
    for (const [key, child] of Object.entries(value)) {
      flatten(child, path ? `${path}.${key}` : key, out);
    }
    return;
  }
  const text = value === null ? 'null' : String(value);
  out.push({
    path,
    value: text.length > MAX_FIELD_LENGTH ? `${text.slice(0, MAX_FIELD_LENGTH)}…` : text,
  });
}

function approval(
  kind: TypedDataApprovalKind,
  token: unknown,
  spender: unknown,
  amount: unknown,
  deadline: unknown
): TypedDataApproval | null {
  const tokenAddr = toAddress(token);
  const spenderAddr = toAddress(spender);
  const value = toUint(amount);
  if (!tokenAddr || !spenderAddr || value === null) return null;
  // An absent deadline means "no expiry"; a present but unreadable one is not decoded.
  const deadlineValue = deadline === undefined ? null : toUint(deadline);
  if (deadline !== undefined && deadlineValue === null) return null;
  return {
    kind,
    token: tokenAddr,
    spender: spenderAddr,
    amount: value.toString(),
    unlimited: value >= UNLIMITED_THRESHOLD,
    deadline: deadlineValue === null ? null : deadlineValue.toString(),
  };
}

function isPermit2Domain(verifyingContract: string | null, domainName: string | null): boolean {
  return verifyingContract === PERMIT2_ADDRESS || domainName === 'Permit2';
}

/**
 * A token permission by its hashed primary type or its domain: EIP-2612 /
 * DAI `Permit`, every Permit2 type (PermitSingle, PermitBatch,
 * Permit[Batch][Witness]TransferFrom, …), or anything for the Permit2 contract.
 */
function isPermitShaped(
  primaryType: string,
  verifyingContract: string | null,
  domainName: string | null
): boolean {
  return /permit/i.test(primaryType) || isPermit2Domain(verifyingContract, domainName);
}

function decodeApprovals(
  primaryType: string,
  verifyingContract: string | null,
  domainName: string | null,
  message: Record<string, Json>
): { approvals: TypedDataApproval[]; failed: boolean } {
  const out: TypedDataApproval[] = [];
  let failed = false;
  const isPermit2 = isPermit2Domain(verifyingContract, domainName);
  const push = (a: TypedDataApproval | null) => {
    if (a) out.push(a);
    else failed = true;
  };

  if (isPermit2) {
    const spender = message['spender'];
    if (primaryType === 'PermitSingle' && isRecord(message['details'])) {
      const d = message['details'];
      push(
        approval(
          'permit2_single',
          d['token'],
          spender,
          d['amount'],
          d['expiration'] ?? message['sigDeadline']
        )
      );
    } else if (primaryType === 'PermitBatch' && Array.isArray(message['details'])) {
      for (const d of message['details']) {
        push(
          isRecord(d)
            ? approval(
                'permit2_batch',
                d['token'],
                spender,
                d['amount'],
                d['expiration'] ?? message['sigDeadline']
              )
            : null
        );
      }
    } else if (primaryType.startsWith('PermitBatch') && Array.isArray(message['permitted'])) {
      for (const p of message['permitted']) {
        push(
          isRecord(p)
            ? approval(
                'permit2_batch_transfer',
                p['token'],
                spender,
                p['amount'],
                message['deadline']
              )
            : null
        );
      }
    } else if (primaryType.startsWith('Permit') && isRecord(message['permitted'])) {
      const p = message['permitted'];
      push(approval('permit2_transfer', p['token'], spender, p['amount'], message['deadline']));
    }
    return { approvals: out, failed };
  }

  if (primaryType === 'Permit') {
    if ('value' in message) {
      // EIP-2612: token is the verifying contract.
      push(
        approval(
          'permit',
          verifyingContract,
          message['spender'],
          message['value'],
          message['deadline']
        )
      );
    } else if ('allowed' in message) {
      // DAI-style: all-or-nothing allowance.
      const tokenAddr = verifyingContract;
      const spenderAddr = toAddress(message['spender']);
      const expiry = message['expiry'] === undefined ? null : toUint(message['expiry']);
      if (tokenAddr && spenderAddr && (message['expiry'] === undefined || expiry !== null)) {
        // Signed as ethers encodes a bool: any truthy value (even "false") grants.
        const allowed = toBoolLikeEthers(message['allowed']);
        out.push({
          kind: 'dai_permit',
          token: tokenAddr,
          spender: spenderAddr,
          amount: null,
          unlimited: allowed,
          deadline: expiry === null ? null : expiry.toString(),
        });
      } else {
        failed = true;
      }
    }
  }
  return { approvals: out, failed };
}

/** Decode a typed-data JSON string as received from eth_signTypedData*. */
export function decodeTypedData(json: string): TypedDataParse {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return { ok: false, error: 'INVALID_JSON' };
  }
  if (!isRecord(parsed)) return { ok: false, error: 'INVALID_SHAPE' };
  const domain = isRecord(parsed['domain']) ? parsed['domain'] : null;
  const message = isRecord(parsed['message']) ? parsed['message'] : null;
  const declaredPrimaryType =
    typeof parsed['primaryType'] === 'string' ? parsed['primaryType'] : null;
  if (!domain || !message || !declaredPrimaryType) return { ok: false, error: 'INVALID_SHAPE' };
  // What is hashed is the root of `types`, whatever `primaryType` claims.
  const derivedPrimaryType = derivePrimaryType(parsed['types']);
  const primaryType = derivedPrimaryType ?? declaredPrimaryType;

  const chainId = parseChainId(domain['chainId']);
  if (chainId === 'invalid') return { ok: false, error: 'INVALID_CHAIN_ID' };
  const verifyingContract = toAddress(domain['verifyingContract']);
  const domainName = typeof domain['name'] === 'string' ? domain['name'] : null;

  const fields: Array<{ path: string; value: string }> = [];
  flatten(message, '', fields);

  const { approvals, failed } = decodeApprovals(
    primaryType,
    verifyingContract,
    domainName,
    message
  );
  const permitShaped =
    isPermitShaped(primaryType, verifyingContract, domainName) ||
    isPermitShaped(declaredPrimaryType, verifyingContract, domainName);
  let undecodable: TypedDataUndecodable | null = null;
  if (derivedPrimaryType === null) undecodable = 'TYPES_INVALID';
  else if (derivedPrimaryType !== declaredPrimaryType) undecodable = 'PRIMARY_TYPE_MISMATCH';
  else if (permitShaped && (approvals.length === 0 || failed)) undecodable = 'PERMIT_NOT_DECODED';

  return {
    ok: true,
    data: {
      primaryType,
      domain: {
        name: domainName,
        version: typeof domain['version'] === 'string' ? domain['version'] : null,
        chainId,
        verifyingContract,
      },
      approvals,
      isOrder: /order/i.test(primaryType),
      fields,
      undecodable,
    },
  };
}

export type TypedDataChainCheck = 'match' | 'mismatch' | 'missing';

/**
 * Typed data bound to another chain is refused (a vault signature there could
 * authorise something on a chain the signers never reviewed). A domain with
 * no chainId (e.g. Snapshot) is allowed but surfaced as "missing".
 */
export function checkTypedDataChain(
  decoded: DecodedTypedData,
  vaultChainId: number | undefined
): TypedDataChainCheck {
  if (decoded.domain.chainId === null) return 'missing';
  return vaultChainId !== undefined && decoded.domain.chainId === vaultChainId
    ? 'match'
    : 'mismatch';
}
